import React, { FC, useEffect, useState } from 'react';
import { AchInfoFormState } from '../../ach-info/ach-info/ach-info';
import { CardInfoFormState } from '../../card-info/card-info/card-info';
import { PGCardInfoFormState } from '../../card-info/pg-card-info/pg-card-info';
import { PaymentAmountFormState } from '../../payment-amount/payment-amount';
import styles from './payment-method-container.less';
import classnames from 'classnames';
import { ConnectedBankAccount, PaymentMethodInfo, PaymentMethodType } from 'src/invoice/types/types';

import { PaymentMethods } from './payment-methods';
import { SavedPaymentMethod } from './saved-payment-method';
import { SavedPaymentMethod as MobileSavedPaymentMethod } from '../mobile/saved-payment-method';
import { BodyText, IconNameType, Spinner } from '@servicetitan/design-system';
import { paymentMethodsContainer } from 'src/payment-method/ioc';
import { SavedPaymentMethodsStore } from 'src/payment-method/stores/saved-payment-methods.store';
import { ChargeType, SplitInfo } from 'src/api';

interface PaymentMethodProps<Params extends { [K in keyof Params]?: string } = {}> {
    paymentAmountData: PaymentAmountFormState;
    cardInfoData: CardInfoFormState;
    pgcardInfoData: PGCardInfoFormState;
    achInfoData: AchInfoFormState;
    usePaymentGateway: () => boolean;
    showLoginLink: boolean;
    setPaymentMethodValid: (isValid: boolean) => void;
    setSecurityCodeValid: (isValid: boolean) => void;
    setPaymentMethodAction: (action: () => void) => void;
    setConnectedAccounts: (accounts: ConnectedBankAccount[]) => void;
    setPaymentButtonTextIcon: (text: string, icon?: IconNameType | undefined) => void;
    invoiceId?: string;
    splits?: SplitInfo[];
    setPaymentMethodSubmit?: (submit: () => void) => void;
    chargeType: ChargeType;
    statementId?: string;
    cardView?: boolean;
    setPaymentMethodType?: ((paymentMethodType: PaymentMethodType) => void) | undefined;
};


export const PaymentMethodContainer: FC<PaymentMethodProps> = ({
    paymentAmountData,
    cardInfoData,
    pgcardInfoData,
    achInfoData,
    usePaymentGateway,
    setPaymentMethodValid,
    setSecurityCodeValid,
    setPaymentMethodAction,
    setConnectedAccounts,
    setPaymentButtonTextIcon,
    showLoginLink,
    invoiceId,
    setPaymentMethodSubmit,
    splits,
    chargeType,
    statementId,
    setPaymentMethodType, cardView = true
}) => {
    const store = paymentMethodsContainer.get(SavedPaymentMethodsStore);

    const [savePaymentChecked, setSavePaymentChecked] = useState(store.getIsLoggedIn());
    const [useAsPrimaryPaymentChecked, setUseAsPrimaryPaymentChecked] = useState(store.getIsLoggedIn());
    const [savedPayment, setSavedPayment] = useState<PaymentMethodInfo | undefined>(undefined);

    const isLoggedIn = store.getIsLoggedIn();

    const isSavedPaymentOptionEnabled = store.getIsSavedPaymentOptionEnabled();

    const [errorMessage, setErrorMessage] = useState<string | any>(undefined);

    const [isReady, setIsReady] = useState(false);

    const prepareSelectedPaymentMethod = async () => {
        if (savedPayment && isLoggedIn) {
            setPaymentMethodValid(!savedPayment.disconnected);
            setErrorMessage(savedPayment.disconnected ?
                'Please update your payment method to continue.' : undefined);
            setPaymentButtonTextIcon(`Pay with ${savedPayment.name} Ending in *${savedPayment.number}`);
            if (setPaymentMethodType) {
                setPaymentMethodType(savedPayment.paymentMethodType);
            }
        }
    };

    useEffect(() => {
        store.setOnSelectedPaymentMethodChange(paymentMethod => {
            setSavedPayment(paymentMethod);
            setIsReady(true);
        });
        store.setOnPbbErrorMessage(error => {
            setErrorMessage(error);
        });
        store.currentInvoiceId = invoiceId;
        store.currentStatementId = statementId;
        store.currentSplits = splits;
        store.init(() => {
            if (!store.getIsLoggedIn() || !store.getIsSavedPaymentOptionEnabled()) {
                // Set ready when not signed-in or no saved payment option exists
                setIsReady(true);
            }
        });
    }, [store.init]);

    useEffect(() => {
        prepareSelectedPaymentMethod();
    }, [savedPayment]);

    return (isReady ?
        (<div className={classnames('card', styles.card, cardView ? '' : styles.nonCardView)}>
            <h3>Payment Method</h3>

            {isLoggedIn && !savedPayment && (
                <span>There are no saved payment methods associated with this account. When submitting a payment, you can choose to save the payment information for a faster checkout in the future.</span>
            )}

            {errorMessage && (
                <div className={classnames('payment-methods-pay-by-bank-error', styles.paymentMethodsPayByBankError)}>
                    <div className={classnames('pay-by-bank-error-header', styles.payByBankErrorHeader)}>
                        <img className={classnames('pay-by-bank-error-warning', styles.payByBankErrorWarning)}
                            src="/assets/images/error-exclamation.svg" />
                        <span className={classnames('pay-by-bank-error-header-title', styles.payByBankErrorHeaderTitle)}>
                            Payment Method Error
                        </span>
                    </div>
                    <div className={classnames('pay-by-bank-error-body', styles.payByBankErrorBody)}>
                        {errorMessage}
                    </div>
                </div>)}

            {isLoggedIn && savedPayment ?
                (cardView ? <SavedPaymentMethod
                    paymentMethod={savedPayment}
                    setPaymentMethod={setSavedPayment}
                    usePaymentGateway={usePaymentGateway}
                    paymentAmountData={paymentAmountData}
                    setPaymentMethodAction={setPaymentMethodAction}
                    setSecurityCodeValid={setSecurityCodeValid}
                    invoiceId={invoiceId}
                    splits={splits}
                    chargeType={chargeType}
                    statementId={statementId}
                    store={store}
                /> : <MobileSavedPaymentMethod
                    paymentMethod={savedPayment}
                    setPaymentMethod={setSavedPayment}
                    usePaymentGateway={usePaymentGateway}
                    paymentAmountData={paymentAmountData}
                    setPaymentMethodAction={setPaymentMethodAction}
                    setSecurityCodeValid={setSecurityCodeValid}
                    invoiceId={invoiceId}
                    splits={splits}
                    chargeType={chargeType}
                    statementId={statementId}
                    store={store}
                />) :
                (<PaymentMethods
                    invoiceId={invoiceId}
                    splits={splits}
                    statementId={statementId}
                    chargeType={chargeType}
                    paymentAmountData={paymentAmountData}
                    cardInfoData={cardInfoData}
                    pgcardInfoData={pgcardInfoData}
                    achInfoData={achInfoData}
                    usePaymentGateway={usePaymentGateway}
                    showLoginLink={showLoginLink}
                    setPaymentMethodValid={setPaymentMethodValid}
                    setPaymentMethodAction={setPaymentMethodAction}
                    setConnectedAccounts={setConnectedAccounts}
                    setPaymentButtonTextIcon={setPaymentButtonTextIcon}
                    showPrimaryOption={isSavedPaymentOptionEnabled}
                    showSavePaymentOption={isSavedPaymentOptionEnabled}
                    savePaymentChecked={savePaymentChecked}
                    useAsPrimaryPaymentChecked={useAsPrimaryPaymentChecked}
                    setSavePaymentChecked={setSavePaymentChecked}
                    setUseAsPrimaryPaymentChecked={setUseAsPrimaryPaymentChecked}
                    paymentMethodStore={store}
                    setPaymentMethodSubmit={setPaymentMethodSubmit}
                    setPaymentMethodType={setPaymentMethodType}
                />)}
        </div>) :
        <div>
            <Spinner className={styles.spinner} />
            <BodyText size="medium" className={styles.loadingMessage}>
                Loading payment method options...
            </BodyText>
        </div>)
}